import { useEffect, useRef } from 'react';

// Default to 5 seconds
const defaultTimeInterval = 5 * 1000;

interface Params {
    onActive?: () => void;
    onInactive?: () => void;
    interval?: number;
}

export default function useDocumentActive(params: Params) {
    const { onActive, onInactive, interval = defaultTimeInterval } = params;

    const activePrevTime = useRef(new Date().valueOf());
    const inactivePrevTime = useRef(new Date().valueOf());

    useEffect(() => {
        const handleActive = () => {
            const currentTime = new Date().valueOf();
            if (currentTime - activePrevTime.current > interval && onActive) {
                activePrevTime.current = currentTime;
                onActive();
            }
        };
        const handleInactive = () => {
            const currentTime = new Date().valueOf();
            if (currentTime - inactivePrevTime.current > interval && onInactive) {
                inactivePrevTime.current = currentTime;
                onInactive();
            }
        };
        const handleVisibility = () => {
            if (document.hidden) {
                handleInactive();
            } else {
                handleActive();
            }
        };

        // Need to use older focus and blur to identify switching window
        window.addEventListener('focus', handleActive);
        window.addEventListener('blur', handleInactive);
        // Check out https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            window.removeEventListener('focus', handleActive);
            window.removeEventListener('blur', handleInactive);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [onActive, onInactive, interval]);
}
